<template>
  <div data-testid="statement-filters" class="filters__container">
    <Link
      v-if="hasAppliedFilters"
      class="filters__clear-button"
      :disabled="disabled"
      @click="clearFilters"
    >
      Limpar filtro
    </Link>
    <FilterSelect
      v-model="filtersValue.movementType"
      data-testid="type-filter"
      class="filters__item"
      label="Movimentações"
      use-option-label
      :disabled="disabled"
      :options="typesOptions"
      @change="value => handleChange(value, 'type')"
    />
    <FilterSelect
      v-model="filtersValue.period"
      data-testid="date-period-filter"
      class="filters__item"
      label="Período"
      use-option-label
      :disabled="disabled"
      :options="datePeriodsOptions"
      @change="value => handleChange(value, 'period')"
    />
    <FilterSelect
      v-model="filtersValue.module"
      data-testid="module-filter"
      class="filters__item"
      label="Tipo"
      use-option-label
      :disabled="disabled"
      :options="modulesOptions"
      @change="value => handleChange(value, 'module')"
    />
    <ButtonSecondary
      data-testid="export-button"
      label="Exportar"
      icon="EA0230"
      icon-position="right"
      class="filters__item"
      :disabled="disabled"
      @click="openModal"
    />

    <ExportModal
      :is-open="isModalOpen"
      :filters="filters"
      :is-loading="isFetchingDownload"
      @submit="emitStatementDownload"
      @close="closeModal"
    />
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Emit } from 'vue-property-decorator';
import {
  FilterSelect,
  ButtonSecondary,
  Link
} from '@warrenbrasil/nebraska-web';
import { ExportModal } from './components/ExportModal';
import { IFilters, IFiltersValue } from '../../types';
import { parseFilterOptions } from './helpers';
import * as trackEvents from '@/modules/transaction/views/Transactions/track-events';

@Component({
  components: { FilterSelect, ButtonSecondary, Link, ExportModal }
})
export default class StatementFilters extends Vue {
  @Prop({ type: Object, required: true })
  readonly filters!: IFilters;

  @Prop({ type: Boolean, default: false })
  readonly disabled!: boolean;

  @Prop({ type: Boolean, default: false })
  readonly isFetchingDownload!: boolean;

  @Emit('change')
  private emitChange() {
    return {
      filters: this.filtersValue,
      hasAppliedFilters: this.hasAppliedFilters
    };
  }

  @Emit('statement-download')
  emitStatementDownload(filters: IFiltersValue) {
    return filters;
  }

  public isModalOpen = false;

  public filtersValue: IFiltersValue = {
    period: '',
    movementType: '',
    module: ''
  };

  get datePeriodsOptions() {
    return parseFilterOptions(this.filters.datePeriods);
  }

  get typesOptions() {
    return parseFilterOptions(this.filters.types);
  }

  get modulesOptions() {
    return parseFilterOptions(this.filters.modules);
  }

  get hasAppliedFilters() {
    return Object.values(this.filtersValue).some(value => !!value);
  }

  private updateFiltersValue(filters: IFiltersValue) {
    this.filtersValue = filters;
  }

  clearFilters() {
    this.filtersValue = {
      period: '',
      movementType: '',
      module: ''
    };

    this.emitChange();
  }

  openModal() {
    this.isModalOpen = true;
  }

  closeModal() {
    this.isModalOpen = false;
  }

  handleChange(value: string, filter: string) {
    if (value.toUpperCase() === 'ALL') {
      switch (filter) {
        case 'type':
          this.updateFiltersValue({ ...this.filtersValue, movementType: '' });
          break;
        case 'period':
          this.updateFiltersValue({ ...this.filtersValue, period: '' });
          break;
        case 'module':
          this.updateFiltersValue({ ...this.filtersValue, module: '' });
          break;
      }
    }

    this.emitTrackEvents(value, filter);
    this.emitChange();
  }

  emitTrackEvents(value: string, filter: string) {
    switch (filter) {
      case 'type':
        trackEvents.trackSelectDirectionClick(value);
        break;
      case 'period':
        trackEvents.trackSelectPeriodClick(value);
        break;
      case 'module':
        trackEvents.trackSelectTypeClick(value);
        break;
    }
  }
}
</script>

<style lang="less" scoped>
.filters {
  &__clear-button {
    color: @element-primary;
    margin-right: @size-spacing-x400;
  }

  &__container {
    display: flex;
    margin-top: @size-spacing-x400;
    align-items: flex-start;
    flex-wrap: wrap;
    row-gap: @size-spacing-x400;

    @media screen and (min-width: 1024px) {
      margin-top: 0;
      align-items: center;
    }
  }

  &__item {
    margin-left: 0;

    @media screen and (min-width: 1024px) {
      margin-left: @size-spacing-x300;
    }
  }
}
</style>
